import { toast } from '@/hooks/use-toast';
import { type LogContext, logger } from '@/utils/logger';

/**
 * Unified error handling utilities
 * Consolidates error handling patterns used across 99+ files
 */

export interface ErrorHandlerOptions {
  showToast?: boolean;
  logToConsole?: boolean;
  fallbackMessage?: string;
  action?: string;
  context?: LogContext;
}

/**
 * Handles errors consistently across the application
 */
export function handleError(
  error: unknown,
  options: ErrorHandlerOptions = {}
): void {
  const {
    showToast = true,
    logToConsole = true,
    fallbackMessage = 'An unexpected error occurred',
    action = 'performing this action',
    context = 'system',
  } = options;

  const errorMessage = getErrorMessage(error, fallbackMessage);

  if (logToConsole) {
    logger.error(context, `Error ${action}: ${errorMessage}`, {
      error: error instanceof Error ? error.message : String(error),
      action,
    });
  }

  if (showToast) {
    toast({
      title: `Failed to ${action.toLowerCase()}`,
      description: errorMessage,
      variant: 'destructive',
    });
  }
}

/**
 * Extracts user-friendly error message from various error types
 */
export function getErrorMessage(
  error: unknown,
  fallback = 'An unexpected error occurred'
): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }

    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }

    if ('details' in error && typeof error.details === 'string') {
      return error.details;
    }
  }

  return fallback;
}

/**
 * Creates a standardized error handler for async operations
 */
export function createAsyncErrorHandler(
  action: string,
  options: Omit<ErrorHandlerOptions, 'action'> = {}
) {
  return (error: unknown) => handleError(error, { ...options, action });
}

/**
 * Wraps async functions with error handling
 */
export function withErrorHandling<T extends unknown[], R>(
  fn: (...args: T) => Promise<R>,
  action: string,
  options: Omit<ErrorHandlerOptions, 'action'> = {}
) {
  return async (...args: T): Promise<R | null> => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, { ...options, action });
      return null;
    }
  };
}

/**
 * Extracts status code from error object if present
 * Returns the status property if it exists, otherwise undefined
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}
